import type { EmploymentAdminPhase } from './employmentJourneyStatus';
import { isAttemptCapForward, isGigPlatformForward } from './employmentJourneyStatus';
import type { MutationSeverity } from './diffRequestSnapshots';

export function employmentForwardBannerCopy(input: {
  lastActionResult?: Record<string, unknown> | null;
}): string {
  if (isAttemptCapForward(input)) {
    return 'Customer advanced after the employment retry cap was reached. Employment remains under review.';
  }
  if (isGigPlatformForward(input)) {
    return 'Customer advanced under review based on gig/platform employer policy. Employment verification is still open for ops review.';
  }
  return 'Customer advanced under review while employment verification completes.';
}

export function employmentPhaseLabel(phase: EmploymentAdminPhase): string {
  switch (phase) {
    case 'forwarded_under_review':
      return 'Forwarded under review';
    case 'retryable_review':
      return 'Needs review — customer can retry';
    case 'verified':
      return 'Employment verified';
    case 'rejected':
      return 'Employment rejected';
    default:
      return 'Employment not complete';
  }
}

export function mutationSeverityLabel(severity: MutationSeverity): string {
  switch (severity) {
    case 'blocking':
      return 'Integrity concern';
    case 'advisory':
      return 'Expected correction';
    default:
      return 'Changed';
  }
}

export function employmentLastActionOperatorCopy(input: {
  lastAction?: string | null;
  lastActionOutcome?: string | null;
  milestones?: string[];
  lastActionResult?: Record<string, unknown> | null;
}): string | null {
  const action = (input.lastAction ?? '').trim();
  if (action !== 'EMPLOYMENT_RUN' && action !== 'EMPLOYMENT_REUSE_PRIOR') {
    return null;
  }

  if (isAttemptCapForward(input)) {
    return 'Last employment action succeeded because the retry cap forwarded the customer under review.';
  }
  if (isGigPlatformForward(input)) {
    return 'Last employment action succeeded under gig/platform forward-under-review policy.';
  }
  if ((input.milestones ?? []).includes('EMPLOYMENT_PENDING_REVIEW')) {
    return 'Customer holds EMPLOYMENT_PENDING_REVIEW — they may continue while ops finishes review.';
  }
  if ((input.lastActionOutcome ?? '').trim().toUpperCase() === 'FAILURE') {
    return 'Last employment action failed — customer should still be on confirm/retry.';
  }
  if ((input.lastActionOutcome ?? '').trim().toUpperCase() === 'SUCCESS') {
    return 'Last employment action completed successfully.';
  }
  return null;
}
